import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { sanitizeHtml } from 'shared/utils/safeHtml';
import {
  AlignLeft, ChevronDown, Clipboard, Columns2, Download, Languages,
} from 'lucide-react';
import {
  FIELD_VIEW_OPTIONS,
  isViewReadOnly,
  parseValueForField,
  reformatDisplayText,
  valueToDisplayText,
  valueToHtmlDocument,
} from 'features/datasets/utils/fieldTextFormat';

function LineNumberedEditor({
  text, onChange, onBlur, readOnly, placeholder, wordWrap,
}) {
  const lines = String(text || '').split('\n');
  const lineCount = Math.max(lines.length, 1);

  return (
    <div className="ds-code-block">
      <div className="ds-code-gutter">
        {Array.from({ length: lineCount }, (_, i) => (
          <div key={i}>{i + 1}</div>
        ))}
      </div>
      {readOnly ? (
        <pre className={`ds-code-pre ${wordWrap ? 'ds-code-wrap' : ''}`}>{text || placeholder || 'null'}</pre>
      ) : (
        <textarea
          className={`ds-code-editor ${wordWrap ? 'ds-code-wrap' : ''}`}
          value={text}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          spellCheck={false}
          placeholder={placeholder}
          rows={Math.max(3, lineCount)}
        />
      )}
    </div>
  );
}

function HtmlPreview({ value }) {
  // Row values come from ingested spans (public site_id) and users: never raw.
  const html = useMemo(() => sanitizeHtml(valueToHtmlDocument(value)), [value]);
  return (
    <div
      className="ds-html-preview"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function ViewTypeMenu({ fieldKind, viewType, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const options = FIELD_VIEW_OPTIONS[fieldKind] || FIELD_VIEW_OPTIONS.input;
  const current = options.find((o) => o.id === viewType) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="ds-field-view-menu" ref={ref}>
      <button
        type="button"
        className="ds-field-view-trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        {current.label}
        <ChevronDown size={12} />
      </button>
      {open ? (
        <div className="ds-field-view-dropdown">
          <div className="ds-field-view-dropdown-title">Select view type</div>
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`ds-field-view-option ${opt.id === viewType ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onChange(opt.id);
                setOpen(false);
              }}
            >
              <span>{opt.id === viewType ? '✓' : ''}</span>
              <span className="flex-1 text-left">{opt.label}</span>
              {opt.readOnly ? <span className="ds-field-view-ro">Read only</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FieldToolbar({
  onCopy, onWrap, onReformat, onDownload, onSplit, wordWrap, splitView,
}) {
  const btn = (title, Icon, onClick, active = false) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`ds-field-tool-btn ${active ? 'active' : ''}`}
    >
      <Icon size={13} />
    </button>
  );

  return (
    <div className="ds-field-toolbar">
      {btn('Copy', Clipboard, onCopy)}
      {btn('Toggle word wrap', AlignLeft, onWrap, wordWrap)}
      {btn('Reformat', Languages, onReformat)}
      {btn('Download', Download, onDownload)}
      {btn('Split view', Columns2, onSplit, splitView)}
    </div>
  );
}

export default function AccordionFieldBlock({
  title,
  icon: Icon,
  fieldKind = 'input',
  defaultOpen = true,
  value,
  onChange,
  viewType: controlledViewType,
  onViewTypeChange,
  error,
  readOnly: forcedReadOnly = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [internalViewType, setInternalViewType] = useState(controlledViewType || 'yaml');
  const [editText, setEditText] = useState('');
  const [wordWrap, setWordWrap] = useState(true);
  const [splitView, setSplitView] = useState(false);
  const valueRef = useRef(value);

  const viewType = controlledViewType ?? internalViewType;
  const setViewType = onViewTypeChange ?? setInternalViewType;
  const readOnly = forcedReadOnly || isViewReadOnly(viewType, fieldKind);

  const syncTextFromValue = useCallback((nextValue, nextView) => {
    setEditText(valueToDisplayText(nextValue, nextView, fieldKind));
  }, [fieldKind]);

  const valueKey = useMemo(() => JSON.stringify(value), [value]);

  useEffect(() => {
    valueRef.current = value;
    syncTextFromValue(value, viewType);
  }, [valueKey, viewType, syncTextFromValue]);

  const applyEdit = useCallback(() => {
    if (readOnly) return;
    try {
      const next = parseValueForField(editText, viewType, fieldKind);
      valueRef.current = next;
      onChange?.(next);
    } catch (e) {
      onChange?.(valueRef.current, e.message);
    }
  }, [editText, viewType, fieldKind, readOnly, onChange]);

  const handleViewChange = (nextView) => {
    if (nextView === viewType) return;
    if (!readOnly) {
      try {
        const parsed = parseValueForField(editText, viewType, fieldKind);
        valueRef.current = parsed;
        onChange?.(parsed);
        syncTextFromValue(parsed, nextView);
      } catch {
        syncTextFromValue(valueRef.current, nextView);
      }
    } else {
      syncTextFromValue(valueRef.current, nextView);
    }
    setViewType(nextView);
  };

  const previewText = useMemo(
    () => valueToDisplayText(value, 'pretty', fieldKind),
    [value, fieldKind],
  );

  const handleCopy = async () => {
    const text = viewType === 'html'
      ? valueToDisplayText(valueRef.current, 'html', fieldKind)
      : editText;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  const handleReformat = () => {
    if (readOnly || (viewType !== 'yaml' && viewType !== 'json')) return;
    try {
      const next = reformatDisplayText(editText, viewType);
      setEditText(next);
      const parsed = parseValueForField(next, viewType, fieldKind);
      valueRef.current = parsed;
      onChange?.(parsed);
    } catch (e) {
      onChange?.(valueRef.current, e.message);
    }
  };

  const handleDownload = () => {
    const ext = viewType === 'json' || viewType === 'pretty' ? 'json' : 'yaml';
    const blob = new Blob([editText || ''], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${title.toLowerCase().replace(/\s+/g, '-')}.${ext}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const renderEditor = (text, opts = {}) => (
    <LineNumberedEditor
      text={text}
      onChange={opts.onChange ?? setEditText}
      onBlur={opts.onBlur ?? applyEdit}
      readOnly={opts.readOnly ?? readOnly}
      wordWrap={wordWrap}
    />
  );

  const body = viewType === 'html' ? (
    <HtmlPreview value={valueRef.current} />
  ) : (
  <div className={splitView ? 'ds-field-split' : ''}>
    {renderEditor(editText)}
    {splitView ? (
      <div className="ds-field-split-pane">
        <div className="ds-field-split-label">Preview</div>
        {renderEditor(previewText, { readOnly: true, onChange: undefined, onBlur: undefined })}
      </div>
    ) : null}
  </div>
  );

  return (
    <div className="ds-accordion">
      <button type="button" className="ds-accordion-header" onClick={() => setOpen((v) => !v)}>
        {Icon ? <Icon size={14} className="shrink-0 text-[color:var(--text-muted)]" /> : null}
        <span>{title}</span>
        <ChevronDown size={14} className={`ds-accordion-chevron ${open ? 'open' : ''}`} />
      </button>
      {open ? (
        <div className="ds-accordion-body">
          <div className="ds-field-toolbar-row">
            <ViewTypeMenu fieldKind={fieldKind} viewType={viewType} onChange={handleViewChange} />
            <FieldToolbar
              wordWrap={wordWrap}
              splitView={splitView}
              onCopy={handleCopy}
              onWrap={() => setWordWrap((v) => !v)}
              onReformat={handleReformat}
              onDownload={handleDownload}
              onSplit={() => setSplitView((v) => !v)}
            />
          </div>
          {body}
          {error ? <div className="mt-1 text-[11px] text-[color:var(--feedback-error)]">{error}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
